import React, { useState, useEffect } from 'react';
import { ResponsiveHelper } from '../../utils/accessibility';
import { spacing } from './tokens';

interface ResponsiveContainerProps {
  children: React.ReactNode;
  /** Custom breakpoint behavior */
  breakpoints?: Partial<Record<keyof typeof ResponsiveHelper.breakpoints, React.CSSProperties>>;
  /** Base styles applied to all breakpoints */
  baseStyle?: React.CSSProperties;
  /** CSS class name */
  className?: string;
  /** HTML element type */
  as?: keyof JSX.IntrinsicElements;
}

const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  breakpoints = {},
  baseStyle = {},
  className,
  as: Element = 'div',
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof ResponsiveHelper.breakpoints>(
    ResponsiveHelper.getCurrentBreakpoint()
  );

  useEffect(() => {
    const handleResize = () => {
      setCurrentBreakpoint(ResponsiveHelper.getCurrentBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get styles for current breakpoint and smaller breakpoints (mobile-first)
  const getResponsiveStyles = (): React.CSSProperties => {
    const breakpointOrder: (keyof typeof ResponsiveHelper.breakpoints)[] = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
    const currentIndex = breakpointOrder.indexOf(currentBreakpoint);
    
    let styles: React.CSSProperties = { ...baseStyle };
    
    // Apply styles from smallest to current breakpoint (mobile-first approach)
    for (let i = 0; i <= currentIndex; i++) {
      const bp = breakpointOrder[i];
      if (bp && breakpoints[bp]) {
        const bpStyles = breakpoints[bp];
        if (bpStyles) {
          styles = { ...styles, ...bpStyles };
        }
      }
    }
    
    return styles;
  };

  const responsiveStyles = getResponsiveStyles();

  return React.createElement(
    Element,
    {
      className,
      style: responsiveStyles,
    },
    children
  );
};

/**
 * Pre-configured responsive grid container
 */
interface ResponsiveGridProps {
  children: React.ReactNode;
  /** Number of columns at different breakpoints */
  columns?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    xxl?: number;
  };
  /** Gap between grid items */
  gap?: keyof typeof spacing;
  /** Custom styles */
  style?: React.CSSProperties;
  className?: string;
}

export const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  children,
  columns = { xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 },
  gap = 'lg',
  style,
  className,
}) => {
  const breakpointStyles: Partial<Record<keyof typeof ResponsiveHelper.breakpoints, React.CSSProperties>> = {
    xs: {
      display: 'grid',
      gridTemplateColumns: `repeat(${columns.xs || 1}, 1fr)`,
      gap: typeof spacing[gap] === 'string' ? spacing[gap] : '16px',
    },
    sm: columns.sm ? {
      gridTemplateColumns: `repeat(${columns.sm}, 1fr)`,
    } : {},
    md: columns.md ? {
      gridTemplateColumns: `repeat(${columns.md}, 1fr)`,
    } : {},
    lg: columns.lg ? {
      gridTemplateColumns: `repeat(${columns.lg}, 1fr)`,
    } : {},
    xl: columns.xl ? {
      gridTemplateColumns: `repeat(${columns.xl}, 1fr)`,
    } : {},
    xxl: columns.xxl ? {
      gridTemplateColumns: `repeat(${columns.xxl}, 1fr)`,
    } : {},
  };

  return (
    <ResponsiveContainer
      breakpoints={breakpointStyles}
      baseStyle={style}
      className={className}
    >
      {children}
    </ResponsiveContainer>
  );
};

/**
 * Responsive table wrapper that converts to cards on mobile
 */
interface ResponsiveTableProps {
  children: React.ReactNode;
  /** Breakpoint at which to switch to card layout */
  switchBreakpoint?: keyof typeof ResponsiveHelper.breakpoints;
  className?: string;
  style?: React.CSSProperties;
}

export const ResponsiveTable: React.FC<ResponsiveTableProps> = ({
  children,
  switchBreakpoint = 'md',
  className,
  style,
}) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof ResponsiveHelper.breakpoints>(
    ResponsiveHelper.getCurrentBreakpoint()
  );

  useEffect(() => {
    const handleResize = () => {
      setCurrentBreakpoint(ResponsiveHelper.getCurrentBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = !ResponsiveHelper.isBreakpoint(switchBreakpoint, '>=');

  const tableStyles: React.CSSProperties = {
    width: '100%',
    overflowX: isMobile ? 'auto' : 'visible',
    ...style,
  };

  return (
    <div 
      className={className}
      style={tableStyles}
    >
      {children}
    </div>
  );
};

/**
 * Responsive text component that adjusts font size
 */
interface ResponsiveTextProps {
  children: React.ReactNode;
  /** Font sizes at different breakpoints */
  sizes?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
    xxl?: string;
  };
  /** HTML element type */
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
}

export const ResponsiveText: React.FC<ResponsiveTextProps> = ({
  children,
  sizes = { xs: '14px', sm: '14px', md: '16px', lg: '16px', xl: '16px', xxl: '16px' },
  as = 'span',
  className,
  style,
}) => {
  const breakpointStyles = {
    xs: { fontSize: sizes.xs },
    sm: sizes.sm ? { fontSize: sizes.sm } : {},
    md: sizes.md ? { fontSize: sizes.md } : {},
    lg: sizes.lg ? { fontSize: sizes.lg } : {},
    xl: sizes.xl ? { fontSize: sizes.xl } : {},
    xxl: sizes.xxl ? { fontSize: sizes.xxl } : {},
  };

  return (
    <ResponsiveContainer
      as={as}
      breakpoints={breakpointStyles}
      baseStyle={style}
      className={className}
    >
      {children}
    </ResponsiveContainer>
  );
};

/**
 * Hook for responsive behavior in components
 */
export const useResponsive = () => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof ResponsiveHelper.breakpoints>(
    ResponsiveHelper.getCurrentBreakpoint()
  );

  useEffect(() => {
    const handleResize = () => {
      setCurrentBreakpoint(ResponsiveHelper.getCurrentBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    currentBreakpoint,
    isMobile: !ResponsiveHelper.isBreakpoint('md'),
    isTablet: ResponsiveHelper.isBreakpoint('md') && !ResponsiveHelper.isBreakpoint('lg'),
    isDesktop: ResponsiveHelper.isBreakpoint('lg'),
    isBreakpoint: ResponsiveHelper.isBreakpoint,
  };
};

export default ResponsiveContainer;